import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IMultiOptions } from 'src/app/core/interfaces/onboarding/business-profile/business-multi-options.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { selectAllBusinessProfile } from 'src/app/core/store/onboarding/reducers+selectors/business-profile.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-application-summary',
  templateUrl: './application-summary.component.html',
  styleUrls: ['./application-summary.component.scss'],
})
export class ApplicationSummaryComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  displayData: { key: string; value: string }[] = [];
  subscriptions: Subscription[] = [];
  headerText = 'Business Profile Summary';

  constructor(private route: ActivatedRoute, private store: Store<IAppState>) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        const props$ = this.store.select(selectAllBusinessProfile);
        const sub = props$.subscribe((props) => {
          this.buildDisplayPayload(props);
        });
        sub.unsubscribe();
      })
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  populatePageStates(transitions: ITransition[]): void {}
  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.headerText =
      contentDict.get(ContentIDConstants.Question)?.displayText ??
      this.headerText;
  }

  buildDisplayPayload(bpStore: any): void {
    this.displayData = [
      {
        key: 'Legal Form Of Business Name',
        value: bpStore.businessType.value,
      },
      {
        key: 'Industry',
        value: bpStore.businessIndustry.value,
      },
      {
        key: 'Business Description',
        value: this.buildBusinessDescriptionString(bpStore.businessDescription),
      },
      {
        key: 'Current Customers',
        value: this.buildCustomerTypesString(bpStore.customerTypes),
      },
      {
        key: 'State of Organization',
        value: bpStore.stateOfOrg.value,
      },
      {
        key: 'DBA',
        value: bpStore.dba,
      },
      {
        key: 'Address',
        value: bpStore.annualRevenue.value,
      },
      {
        key: 'Website',
        value: bpStore.companyWebsite,
      },
      {
        key: 'Year Founded',
        value: bpStore.yearFounded,
      },
      {
        key: 'Number of Employees',
        value: bpStore.employeeCount.value,
      },
      {
        key: 'Invoicing Method',
        value: bpStore.invoicingMethod.value,
      },
      {
        key: 'Type of Support Needed',
        value: this.buildSupportMethodsString(bpStore.supportMethods),
      },
    ];
  }

  buildCustomerTypesString(customerTypes: IMultiOptions[]): string {
    const customerTypeValues = customerTypes.filter((x) => x.selected);
    const customerLen = customerTypeValues.length;
    let result = '';
    for (let i = 0; i < customerLen; i += 1) {
      if (i === customerLen - 1) {
        result += customerTypeValues[i].value;
      } else {
        result += `${customerTypeValues[i].value}, `;
      }
    }
    return result;
  }

  buildSupportMethodsString(supportMethods: IMultiOptions[]): string {
    const supportMethodValues = supportMethods.filter((x) => x.selected);
    const methodLen = supportMethodValues.length;
    let result = '';
    for (let i = 0; i < methodLen; i += 1) {
      if (i === methodLen - 1) {
        result += supportMethodValues[i].value;
      } else {
        result += `${supportMethodValues[i].value}, `;
      }
    }
    return result;
  }

  buildBusinessDescriptionString(businessDescription: string): string {
    if (businessDescription.length <= 25) return businessDescription;
    return `${businessDescription.substring(0, 22)}...`;
  }
}
